import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import favicon from "./assets/images/favicon.png";
import { useAuthStore } from "./store/authStore";
import { ROUTES, RECAPTCHA_SITE_KEY } from "./constants";
import { ToastContainer, GlobalLoadingWrapper } from "./components/ui";
import { PublicRoute } from "./components/routes/PublicRoute";
import { ProtectedRoute } from "./components/routes/ProtectedRoute";
import { PermissionsProvider } from "./hooks";
import { LicenseOverlayWrapper } from "./components/LicenseOverlayWrapper";
import { EmployeeFormPage } from "./pages/employees/form";
import PublicEmployeePage from "./pages/employees/public";
import { LoginPage } from "./pages/login";
import { ForgotPasswordPage } from "./pages/forgotpassword";
import { DashboardPage } from "./pages/dashboard";
import { EmployeesPage } from "./pages/employees";
import LicensesPage from "./pages/licenses";
import UsersPage from "./pages/users";
import { UserFormPage } from "./pages/users/form";
import { CompanyFormPage } from "./pages/companies/form";
import { CompaniesPage } from "./pages/companies";
import { PackageFormPage } from "./pages/packages/form";
import { PackagesPage } from "./pages/packages";
import { ContactUsFormPage } from "./pages/contactUs/form";
import { ContactUsPage } from "./pages/contactUs";
import LookupsPage from "./pages/lookup";
import { LookupFormPage } from "./pages/lookup/form";
import HomePage from "./pages/website/Home";
import ContactPage from "./pages/website/Contact";
import AboutPage from "./pages/website/About";
import FeaturesPage from "./pages/website/Features";
import PricingPage from "./pages/website/Pricing";
import FaqPage from "./pages/website/Faq";
import PrivacyPage from "./pages/website/Privacy";
import DataPolicyPage from "./pages/website/DataPolicy";
import TermsPage from "./pages/website/Terms";
import { PaymentResultPage } from "./pages/payment/paymentResult";
import { UpgradePackagePage } from "./pages/upgrade/UpgradePackagePage";
import { loadAnalytics, trackPageView } from "./utils/analytics";
import { Home } from "@mui/icons-material";
import { getBasePathWithoutTrailingSlash } from "./utils/basePath";

function App() {
  const initializeAuth = useAuthStore((state) => state.initializeAuth);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  useEffect(() => {
    const reinitialize = () => initializeAuth();
    const onStorage = (e: StorageEvent) => {
      if (!e.key) return;
      const k = e.key.toLowerCase();
      if (
        k === "__auth_event__" ||
        k === "access_token" ||
        k === "refresh_token" ||
        k === "user_data"
      ) {
        initializeAuth();
      }
    };

    window.addEventListener(
      "auth:token-changed",
      reinitialize as EventListener
    );
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(
        "auth:token-changed",
        reinitialize as EventListener
      );
      window.removeEventListener("storage", onStorage);
    };
  }, [initializeAuth]);

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "icon";
    link.type = "image/svg";
    link.href = favicon;
    document.head.appendChild(link);

    document.title = "Qcard";
    loadAnalytics();
  }, []);

  return (
    <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
      <BrowserRouter basename={getBasePathWithoutTrailingSlash()}>
        <RouteChangeTracker />
        <PermissionsProvider>
          <GlobalLoadingWrapper>
            <ToastContainer />
            <LicenseOverlayWrapper />
          <Routes>
            <Route
              path={ROUTES.LOGIN}
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.FORGOTPASSWORD_WITH_ID}
              element={
                <PublicRoute>
                  <ForgotPasswordPage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.FORGOTPASSWORD}
              element={
                <PublicRoute>
                  <ForgotPasswordPage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.REGISTER}
              element={
                <PublicRoute>
                  <CompanyFormPage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.REGISTER_Package}
              element={
                <PublicRoute>
                  <CompanyFormPage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.USER_VIEW}
              element={
                <ProtectedRoute>
                  <UserFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.USER_NEW}
              element={
                <ProtectedRoute>
                  <UserFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.USER_EDIT}
              element={
                <ProtectedRoute>
                  <UserFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.EMPLOYEE_VIEW}
              element={
                <ProtectedRoute>
                  <EmployeeFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.EMPLOYEE_PUBLIC}
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <PublicEmployeePage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.EMPLOYEE_NEW}
              element={
                <ProtectedRoute>
                  <EmployeeFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.EMPLOYEE_EDIT}
              element={
                <ProtectedRoute>
                  <EmployeeFormPage />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.COMPANY_VIEW}
              element={
                <ProtectedRoute>
                  <CompanyFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.COMPANY_NEW}
              element={
                <ProtectedRoute>
                  <CompanyFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.COMPANY_EDIT}
              element={
                <ProtectedRoute>
                  <CompanyFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.COMPANY_PROFILE}
              element={
                <ProtectedRoute>
                  <CompanyFormPage />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.PACKAGE_VIEW}
              element={
                <ProtectedRoute>
                  <PackageFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.PACKAGE_NEW}
              element={
                <ProtectedRoute>
                  <PackageFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.PACKAGE_EDIT}
              element={
                <ProtectedRoute>
                  <PackageFormPage />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.DASHBOARD}
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.UPGRADE_PACKAGE}
              element={
                <ProtectedRoute>
                  <UpgradePackagePage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.EMPLOYEES}
              element={
                <ProtectedRoute>
                  <EmployeesPage />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.COMPANIES}
              element={
                <ProtectedRoute>
                  <CompaniesPage />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.PACKAGES}
              element={
                <ProtectedRoute>
                  <PackagesPage />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.USERS}
              element={
                <ProtectedRoute>
                  <UsersPage />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.LICENSES}
              element={
                <ProtectedRoute>
                  <LicensesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.LOOKUPS}
              element={
                <ProtectedRoute>
                  <LookupsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.LOOKUP_NEW}
              element={
                <ProtectedRoute>
                  <LookupFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.LOOKUP_EDIT}
              element={
                <ProtectedRoute>
                  <LookupFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.HOME}
              element={
              <PublicRoute redirectIfAuthenticated={false}>
                  <HomePage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.CONTACTUS}
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <ContactUsPage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.CONTACTUS_VIEW}
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <ContactUsFormPage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.CONTACT}
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <ContactPage />
                </PublicRoute>
              }
            />
            {/* <Route
              path={ROUTES.ABOUT}
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <AboutPage />
                </PublicRoute>
              }
            />*/}
            <Route
              path={ROUTES.FEATURES}
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <HomePage/>
                </PublicRoute>
              }
            /> 
            <Route
              path={ROUTES.PRICING}
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <PricingPage />
                </PublicRoute>
              }
            />
            {/* <Route
              path={ROUTES.FAQ}
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <FaqPage />
                </PublicRoute>
              }
            /> */}
            <Route
              path={ROUTES.PRIVACY}
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <PrivacyPage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.DATA_POLICY}
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <DataPolicyPage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.TERMS}
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <TermsPage />
                </PublicRoute>
              }
            />
            <Route
              path="/paymentResult"
              element={
                <PublicRoute redirectIfAuthenticated={false}>
                  <PaymentResultPage />
                </PublicRoute>
              }
            /> 
            <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
          </Routes>
        </GlobalLoadingWrapper>
      </PermissionsProvider>
    </BrowserRouter>
    </GoogleReCaptchaProvider>
  );
}

export default App;

function RouteChangeTracker() {
  const location = useLocation();
  useEffect(() => {
  window.scrollTo({ top: 0, behavior: "auto" });
    trackPageView(location.pathname + location.search);
  }, [location]);
  return null;
}
